<template>
  <div class="review-list-setting">
    <template v-if="readOnly">
      <base-section-title title="评审办法" class="m-b-24">
        <template v-slot:right-tool>
          <el-button type="primary" plain @click="exportExcel()">
            一键导出
          </el-button>
        </template>
      </base-section-title>

      <el-form label-suffix="：">
        <el-form-item label="评分设置">
          <span>精确至小数点后{{ decimalPoint }}位</span>
        </el-form-item>
        <el-form-item
          v-if="showScoreOnce"
          label="商务、技术、价格评审是否可同时进行"
        >
          <span>{{ isScoreOnceDict.translate(form.isScoreOnce).label }}</span>
        </el-form-item>
      </el-form>

      <bid-review-way
        v-for="(item, index) in list"
        :id="item.title"
        :ref="`way${item.wayType}`"
        :key="item.wayType"
        v-model="list[index]"
        :already-score="alreadyScore"
        :catalog-list="catalogList"
        :read-only="readOnly"
        :review-model="reviewModel"
        :review-method="reviewMethod"
      />
    </template>
    <template v-else>
      <base-section-title title="设置评审办法" class="m-b-24">
        <template v-slot:right-tool>
          <div class="flex">
            <el-button type="primary" plain @click="openTemplateImportDialog">
              一键导入
            </el-button>
            <el-button type="primary" plain @click="exportExcel()">
              一键导出
            </el-button>
          </div>
        </template>
      </base-section-title>

      <!-- 评审办法设置说明 -->
      <el-collapse class="review-explain" value="explain">
        <el-collapse-item title="评审办法设置说明" name="explain">
          <template v-slot:title>
            <div class="explain-title">评审办法设置说明</div>
          </template>
          <ol>
            <li>
              评审办法设置是用于激活在评审阶段的评审，不勾选则不进行未勾选的评审过程
            </li>
            <li>评审顺序设置用于调整评审过程先后顺序</li>
            <li>你可以通过点击评审流程节点进行手动设置每个评审办法内容</li>
            <li>
              你可以通过右上角导入的方式将评审办法内容导入到表单内，也可以通过一键导出将已添加的评审办法导出到excel表格中。
            </li>
          </ol>
        </el-collapse-item>
      </el-collapse>

      <el-form ref="formRef" :model="form" :rules="formRules" label-suffix="：">
        <el-row :gutter="40">
          <el-col :sm="24" :xl="24">
            <el-form-item label="评分设置" prop="decimalPoint">
              <el-select v-model="form.decimalPoint" @change="onChangeDecimal">
                <el-option
                  v-for="item in [2, 4]"
                  :key="item"
                  :label="`精确至小数点后${item}位`"
                  :value="item"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :sm="24" :xl="24">
            <el-form-item label="评审办法设置">
              <el-checkbox-group
                v-model="form.checkList"
                @change="changeReviewList"
              >
                <el-checkbox
                  v-for="item in reviewWayOption"
                  :key="item.wayType"
                  :label="item.wayType"
                  :disabled="getCheckDisabled(item)"
                >
                  {{ item.title }}
                </el-checkbox>
              </el-checkbox-group>
            </el-form-item>
          </el-col>
          <el-col :sm="24" :xl="24">
            <el-form-item label="评审顺序设置">
              <span class="m-r-8">{{ reviewWaySortString }}</span>
              <el-button type="text" @click="openSortDialog">
                修改评审顺序
              </el-button>
            </el-form-item>
          </el-col>
          <el-col v-if="showScoreOnce" :sm="24" :xl="12">
            <el-form-item label="商务、技术、价格评审是否可同时进行">
              <el-radio-group
                v-model="form.isScoreOnce"
                @change="changeScoreOnce"
              >
                <el-radio
                  v-for="item in isScoreOnceDict.array"
                  :key="item.value"
                  :label="item.value"
                >
                  {{ item.label }}
                </el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div v-if="list.length" class="review-tab-bar">
        <div
          v-for="(item, index) in list"
          :key="item.title"
          class="tab-item"
          :class="{ active: index === activeIndex }"
          @click="handleSelectStep(item, index)"
        >
          {{ item.title }}
        </div>
      </div>
      <bid-review-way
        v-for="(item, index) in list"
        :id="item.title"
        :ref="`way${item.wayType}`"
        :key="item.wayType"
        v-model="list[index]"
        :already-score="alreadyScore"
        :catalog-list="catalogList"
        :review-model="reviewModel"
        :review-method="reviewMethod"
        :decimal-point="decimalPoint"
      />

      <bid-review-sort-dialog
        :visible.sync="sortDialogVisible"
        :list="list"
        :review-model="reviewModel"
        :review-method="reviewMethod"
        @confirm="handleSortConfirm"
      />

      <!-- 评审办法导入弹窗 -->
      <template-import-dialog
        v-model="importDialogVisible"
        title="评审办法导入"
        tip-first="下载评审办法导入模板，根据所提供的模板编辑模板表格"
        tip-second="上传表格，点击确认完成评审办法导入"
        template-name="评审办法模板.xlsx"
        @upload="uploadTemplate"
        @remove="removeTemplate"
        @confirm="confirmImport"
      >
        <template v-slot:download>
          <download-file :download-link="fileName">
            <el-button class="m-l-5 download-btn" type="primary">
              导入模板下载
            </el-button>
          </download-file>
        </template>
      </template-import-dialog>
    </template>
  </div>
</template>

<script>
import BaseSectionTitle from '@/components/common/BaseSectionTitle.vue'
import BidReviewWay from '@/components/kpb/project/BidReviewWay.vue'
import BidReviewSortDialog from '@/components/kpb/project/BidReviewSortDialog.vue'
// import StepComponent from '@/components/common/StepComponent.vue'
import DownloadFile from '@/components/common/DownloadFile.vue'
import TemplateImportDialog from '@/components/common/TemplateImportDialog.vue'
import { cloneDeep } from 'lodash'
import {
  bidReviewMethodTypeDict,
  bidReviewScoringTypeDict,
  reviewWayTypeDict,
  isScoreOnceDict,
  reviewModelDict,
  reviewMethodModelDict
} from '@/dictionary/project/kpb-project'
import {
  exportReviewWaysExcelApi,
  importReviewWaysExcelApi
} from '@/api/procure/kpb-project'
import { countRepeatNum } from '@/utils/utils'
import { moveInArray } from '@/utils/utils'
import { requiredRule } from '@/utils/form-validate'

function getReviewWay(config) {
  return {
    // 名称
    title: config.title,
    // 序号
    orderNum: config.orderNum || 0,
    // 评审办法类型
    wayType: config.wayType,
    // 评审方式
    reviewMethod: config.reviewMethod,
    // 评分方式
    scoreMethod: config.scoreMethod || '',
    // 评审总分
    scoreSum: config.scoreSum || undefined,
    // 评审点列表
    itemList: config.itemList || [],
    // 算分类型
    scoringType: config.scoringType || bidReviewScoringTypeDict.MANUAL.value,
    // 自动算分设置
    autoScoring: config.autoScoring || {}
  }
}

function sortByOrderNum(a, b) {
  if (Number(b.orderNum) > Number(a.orderNum)) {
    return -1
  }
  if (Number(a.orderNum) > Number(b.orderNum)) {
    return 1
  }
  return 0
}

export default {
  name: 'BidReviewListSetting',

  components: {
    DownloadFile,
    BaseSectionTitle,
    BidReviewWay,
    BidReviewSortDialog,
    // StepComponent,
    TemplateImportDialog
  },

  props: {
    reviewList: {
      type: Array,
      default: () => []
    },

    catalogList: {
      type: Array,
      default: () => []
    },

    isScoreOnce: {
      type: [String, Number],
      default: ''
    },

    decimalPoint: {
      type: [String, Number],
      default: ''
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    kpbFileId: {
      type: [String, Number],
      default: ''
    },

    // 评审模式
    reviewModel: {
      type: [String, Number],
      default: reviewModelDict.DEFAULT.value
    },

    // 评审办法
    reviewMethod: {
      type: [String, Number],
      default: reviewMethodModelDict.DEFAULT.value
    }
  },

  data() {
    return {
      isScoreOnceDict,

      form: {
        checkList: [],
        isScoreOnce: '',
        // 小数点精度
        decimalPoint: undefined
      },
      formRules: {
        decimalPoint: requiredRule('打分设置不能为空')
      },
      reviewWayOption: [],
      reviewWayObj: {},
      list: [],
      activeIndex: 0,
      sortDialogVisible: false,

      // 评审办法导入弹窗
      importDialogVisible: false,
      // 导入的数据
      importData: null
    }
  },

  computed: {
    // 已设置评分办法的总分总和
    alreadyScore() {
      const result = this.list.reduce((acc, cur) => {
        return Number(acc) + Number(cur.scoreSum || 0) * 100
      }, 0)

      return result / 100
    },

    reviewWaySortString() {
      return this.list
        .map(
          item =>
            reviewWayTypeDict(this.reviewModel).translate(item.wayType)?.label
        )
        .join('->')
    },

    // 显示同时打分设置
    showScoreOnce() {
      // 双信封不显示
      if (this.isDoubleFile) return false

      // 当有两个及以上的打分型评审时显示
      const methodNumObj = countRepeatNum(this.list, 'reviewMethod')
      const scoreNum = methodNumObj[bidReviewMethodTypeDict.SCORE.value]?.num
      return scoreNum > 1
    },

    // 是否是双信封
    isDoubleFile() {
      return this.reviewModel === reviewModelDict.DOUBLE.value
    },

    // 是否是最高价/最低价法
    isRankByPrice() {
      const byPriceMethod = [
        reviewMethodModelDict.PRICE_HIGHEST.value,
        reviewMethodModelDict.PRICE_LOWEST.value
      ]
      return byPriceMethod.includes(this.reviewMethod)
    },

    // 是综合评分法
    isRankByScore() {
      return this.reviewMethod === reviewMethodModelDict.DEFAULT.value
    },

    fileName() {
      if (!this.reviewMethod) return ''
      const reviewMethodEntry = reviewMethodModelDict.translate(
        this.reviewMethod
      )
      return reviewMethodEntry?.getTemplateFileName(this.reviewModel)
    }
  },

  watch: {
    reviewList: {
      handler(val) {
        if (val?.length) {
          this.list = cloneDeep(val)
          this.form.checkList = this.list.map(item => item.wayType)
        }
      },
      deep: true,
      immediate: true
    },

    isScoreOnce: {
      handler(val) {
        this.form.isScoreOnce = this.isScoreOnce
      },
      immediate: true
    },

    decimalPoint: {
      handler(val) {
        this.form.decimalPoint = val
      },
      immediate: true
    },

    reviewModel() {
      if (!this.readOnly) {
        // 切换双信封，则清空评审办法
        this.form.checkList = []
        this.list = []
        this.initReviewObj()
        // 双信封默认勾选价格分，且不可取消勾选
        if (this.form.checkList.length === 0 && this.isDoubleFile) {
          this.form.checkList.push(
            reviewWayTypeDict(this.reviewModel).QUOTATION.value
          )
          this.changeReviewList()
        }
      }
    },

    reviewMethod() {
      if (!this.readOnly) {
        this.form.checkList = []
        this.list = []
        this.initReviewObj()
        // 双信封默认勾选价格分，且不可取消勾选
        if (this.form.checkList.length === 0 && this.isDoubleFile) {
          this.form.checkList.push(
            reviewWayTypeDict(this.reviewModel).QUOTATION.value
          )
          this.changeReviewList()
        }
      }
    },

    reviewWayOption(val) {
      if (val?.length) {
        this.$emit(
          'getReviewWayOption',
          val.map(item => item.wayType)
        )
      }
    }
  },

  created() {
    this.initReviewObj()
  },

  methods: {
    initReviewObj() {
      this.reviewWayObj = {}
      this.reviewWayOption = []
      // 资格评审
      const qualification = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).QUALIFICATION.value,
        title: reviewWayTypeDict(this.reviewModel).QUALIFICATION.label,
        reviewMethod: bidReviewMethodTypeDict.PASS_OR_NOT.value
      })
      // 符合性评审
      const compliance = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).COMPLIANCE.value,
        title: reviewWayTypeDict(this.reviewModel).COMPLIANCE.label,
        reviewMethod: bidReviewMethodTypeDict.PASS_OR_NOT.value
      })

      // 价格分评分
      const quotation = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).QUOTATION.value,
        title: reviewWayTypeDict(this.reviewModel).QUOTATION.label,
        reviewMethod: bidReviewMethodTypeDict.SCORE.value
      })
      // 技术评分
      const technical = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).TECHNICAL.value,
        title: reviewWayTypeDict(this.reviewModel).TECHNICAL.label,
        reviewMethod: bidReviewMethodTypeDict.SCORE.value
      })
      // 商务评分
      const business = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).BUSINESS.value,
        title: reviewWayTypeDict(this.reviewModel).BUSINESS.label,
        reviewMethod: bidReviewMethodTypeDict.SCORE.value
      })
      // 响应性评审
      const response = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).RESPONSE.value,
        title: reviewWayTypeDict(this.reviewModel).RESPONSE.label,
        reviewMethod: bidReviewMethodTypeDict.PASS_OR_NOT.value
      })
      // 技术评审
      const technicalPassing = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).TECHNICAL_PASSING.value,
        title: reviewWayTypeDict(this.reviewModel).TECHNICAL_PASSING.label,
        reviewMethod: bidReviewMethodTypeDict.PASS_OR_NOT.value
      })
      // 商务评审
      // const businessPassing = getReviewWay({
      //   wayType: reviewWayTypeDict.BUSINESS_PASSING.value,
      //   reviewMethod: bidReviewMethodTypeDict.PASS_OR_NOT.value
      // })
      // 价格分评审
      const quotationPassing = getReviewWay({
        wayType: reviewWayTypeDict(this.reviewModel).QUOTATION_PASSING.value,
        title: reviewWayTypeDict(this.reviewModel).QUOTATION_PASSING.label,
        reviewMethod: bidReviewMethodTypeDict.PASS_OR_NOT.value
      })
      // 依需求手动排序
      let sortList = [qualification, compliance, quotation, technical, business]
      // 双信封
      if (this.isDoubleFile) {
        sortList = [
          qualification,
          compliance,
          response,
          business,
          technicalPassing,
          technical,
          quotationPassing,
          quotation
        ]
      }
      // 最高价/最低价法
      if (this.isRankByPrice) {
        quotation.scoreSum = 100
        sortList = [
          qualification,
          compliance,
          response,
          quotationPassing,
          quotation
        ]
      }
      const list = sortList.map((item, index) => {
        item.orderNum = index + 1
        return item
      })

      this.reviewWayOption = list
      list.forEach(item => {
        this.reviewWayObj[item.wayType] = item
      })
    },

    changeReviewList() {
      // 双信封综合评审中，勾选技术评审，同步勾选技术评分，勾选技术评分时，不需要同步勾选技术评审，商务同理
      if (this.isDoubleFile && this.isRankByScore) {
        const hasTechnicalPassing = this.form.checkList.includes(
          reviewWayTypeDict(this.reviewModel).TECHNICAL_PASSING.value
        )
        const hasTechnical = this.form.checkList.includes(
          reviewWayTypeDict(this.reviewModel).TECHNICAL.value
        )
        if (hasTechnicalPassing && !hasTechnical) {
          this.form.checkList.push(
            reviewWayTypeDict(this.reviewModel).TECHNICAL.value
          )
        }

        // const hasBusinessPassing = this.form.checkList.includes(
        //   reviewWayTypeDict.BUSINESS_PASSING.value
        // )
        // const hasBusiness = this.form.checkList.includes(
        //   reviewWayTypeDict.BUSINESS.value
        // )
        // if (hasBusinessPassing && !hasBusiness) {
        //   this.form.checkList.push(reviewWayTypeDict.BUSINESS.value)
        // }
      }
      const tempList = cloneDeep(this.list)
      const afterList = []
      this.form.checkList.forEach(item => {
        const wayObj = cloneDeep(this.reviewWayObj[item])
        const wayIndex = tempList.findIndex(way => way.wayType === item)
        if (wayIndex > -1) {
          // 需要重置序号，以保证插入顺序
          tempList[wayIndex].orderNum = wayObj.orderNum
          afterList.push(tempList[wayIndex])
        } else {
          afterList.push(wayObj)
        }
      })
      // 如果是双信封评审模式，则需要将价格分放在最后
      if (this.isDoubleFile) {
        const quotationReview = afterList.find(
          item =>
            item.wayType === reviewWayTypeDict(this.reviewModel).QUOTATION.value
        )
        const quotationWayObj = cloneDeep(
          this.reviewWayObj[reviewWayTypeDict(this.reviewModel).QUOTATION.value]
        )
        if (!quotationReview) {
          afterList.push(quotationWayObj)
        } else {
          // 需要重置序号，以保证插入顺序
          quotationReview.orderNum = quotationWayObj.orderNum
        }
      }
      // 根据指定顺序排序
      afterList.sort(sortByOrderNum)
      // 重写评审办法列表中的orderNum
      this.list = afterList.map((item, index) => {
        item.orderNum = index + 1
        return item
      })
      this.activeIndex = this.form.checkList.length

      if (!this.showScoreOnce) {
        this.form.isScoreOnce = isScoreOnceDict.NO.value
        this.changeScoreOnce()
      }
    },

    changeScoreOnce() {
      this.$emit('update:isScoreOnce', this.form.isScoreOnce)
    },

    onChangeDecimal() {
      this.$emit('update:decimalPoint', this.form.decimalPoint)
    },

    handleSelectStep(item, index) {
      document
        .querySelector(`#${item.title}`)
        .scrollIntoView({ block: 'center', behavior: 'smooth' })
      this.activeIndex = index
    },

    openSortDialog() {
      if (!this.list.length) {
        this.$message.warning('请先添加评审办法')
        return
      }
      this.sortDialogVisible = true
    },

    handleSortConfirm(list) {
      list.forEach(item => {
        const wayObj = this.reviewWayObj[item.wayType] || {}
        wayObj.orderNum = item.orderNum
      })
      this.list = list
    },

    openTemplateImportDialog() {
      this.importDialogVisible = true
      this.importData = null
    },

    /**
     * 获取导入的excel
     */
    uploadTemplate(file) {
      if (!file) {
        return false
      }

      importReviewWaysExcelApi({ file })
        .then(res => {
          const reviewWayOptionList = this.reviewWayOption.map(
            item => item.wayType
          )
          const tempImportList = (res.data || [])
            .filter(item => {
              return reviewWayOptionList.includes(item.wayType)
            })
            .map(item => {
              return Object.assign({}, item, {
                title: reviewWayTypeDict(this.reviewModel).translate(
                  item.wayType
                ).label,
                // 所有导入的评审办法默认手动算分
                scoringType: bidReviewScoringTypeDict.MANUAL.value,
                itemList: item.itemList
                  .filter(wayItem => {
                    return !isNaN(Number(wayItem.orderNum))
                  })
                  .map(wayItem => {
                    return Object.assign({}, wayItem, {
                      relationCatalog: {
                        orderNum: '',
                        catalogType: '',
                        catalogName: ''
                      }
                    })
                  })
                  .sort(sortByOrderNum)
                  .map((way, index) => {
                    // 后端导入数据的排序可能从0开始，修正排序从1开始
                    return Object.assign({}, way, { orderNum: index + 1 })
                  })
              })
            })
            .sort(sortByOrderNum)
            .map((way, index) => {
              // 后端导入数据的排序可能从0开始，修正排序从1开始
              return Object.assign({}, way, { orderNum: index + 1 })
            })

          // 双信封评审模式中，价格分评审在最后
          const quotationReviewIndex = tempImportList.findIndex(
            item =>
              item.wayType ===
              reviewWayTypeDict(this.reviewModel).QUOTATION.value
          )
          if (quotationReviewIndex > -1) {
            this.importData = moveInArray(
              tempImportList,
              quotationReviewIndex,
              tempImportList.length - 1
            )
          } else {
            const quotationReview = this.reviewWayObj[
              reviewWayTypeDict(this.reviewModel).QUOTATION.value
            ]
            tempImportList.push(quotationReview)
            this.importData = tempImportList
          }

          if (!this.importData?.length) {
            this.$message.warning('导入模板无数据，请重新上传')
          }
        })
        .catch(err => {
          this.$message.error(err.message || '导入失败')
        })
    },

    /**
     * 移除上传的模板
     */
    removeTemplate() {
      this.importData = null
    },

    confirmImport() {
      if (this.importData?.length > 0) {
        // 双信封最高价/最低价只导入资格、符合和价格分
        if (this.isDoubleFile && this.isRankByPrice) {
          const {
            QUALIFICATION,
            COMPLIANCE,
            QUOTATION,
            RESPONSE
          } = reviewWayTypeDict(this.reviewModel)
          const importWayList = [
            QUALIFICATION,
            COMPLIANCE,
            QUOTATION,
            RESPONSE
          ].map(item => item.value)
          this.list = this.importData
            .filter(item => importWayList.includes(item.wayType))
            .map(item => {
              // 最高价/最低价法中，价格分评审的评审点要清空，评审总分设置为100分
              if (item.wayType === QUOTATION.value) {
                item.itemList = []
                item.scoreMethod = undefined
                item.scoreSum = undefined
              }
              return item
            })
        } else {
          this.list = this.importData
        }
        this.form.checkList = this.list.map(item => item.wayType)
      }
    },

    /**
     * 导出评审办法excel
     */
    async exportExcel() {
      if (this.readOnly) {
        const ids = {
          fileId: this.kpbFileId
        }
        exportReviewWaysExcelApi(ids)
      } else {
        this.$emit('export', this.readOnly)
      }
    },

    validateWayList() {
      return new Promise((resolve, reject) => {
        // 先校验评分表单设置
        this.$refs.formRef.validate(valid => {
          if (!valid) reject()

          // 如果是双信封评审，则至少要有一个非价格分评审
          const passReviewOption = [
            reviewWayTypeDict(this.reviewModel).QUALIFICATION.value,
            reviewWayTypeDict(this.reviewModel).COMPLIANCE.value,
            reviewWayTypeDict(this.reviewModel).TECHNICAL.value,
            reviewWayTypeDict(this.reviewModel).BUSINESS.value,
            reviewWayTypeDict(this.reviewModel).RESPONSE.value
          ]
          const hasOtherReview = this.form.checkList.some(item =>
            passReviewOption.includes(item)
          )
          if (this.isDoubleFile && !hasOtherReview) {
            this.$message.error('请至少添加一项其它评审办法')
            reject()
          }
          Promise.allSettled(
            this.form.checkList.map(item => {
              const ref = this.$refs[`way${item}`][0]
              return ref.validateListForm()
            })
          ).then(res => {
            if (res.some(item => item.status === 'rejected')) {
              reject()
            } else {
              resolve(true)
            }
          })
        })
      })
    },

    /**
     * 当前评审办法勾选项是否禁用
     */
    getCheckDisabled(item) {
      if (this.isDoubleFile) {
        // 双信封模式下，价格分评审禁止操作
        if (
          item.wayType === reviewWayTypeDict(this.reviewModel).QUOTATION.value
        ) {
          return true
        }
        // 双信封模式综合评分下，如果技术评审勾选则禁止取消技术评分，商务同理
        if (this.isRankByScore) {
          if (
            item.wayType === reviewWayTypeDict(this.reviewModel).TECHNICAL.value
          ) {
            const hasTechnicalPassing = this.form.checkList.includes(
              reviewWayTypeDict(this.reviewModel).TECHNICAL_PASSING.value
            )
            return hasTechnicalPassing
          }

          // if (item.wayType === reviewWayTypeDict.BUSINESS.value) {
          //   const hasBusinessPassing = this.form.checkList.includes(
          //     reviewWayTypeDict.BUSINESS_PASSING.value
          //   )
          //   return hasBusinessPassing
          // }
        }
      }

      return false
    },

    /**
     * 获取评审办法列表数据
     */
    getReviewListSetting() {
      return cloneDeep(this.list)
    }
  }
}
</script>

<style scoped lang="scss">
.review-list-setting {
  background: $white-color;
  border-radius: 4px;
  padding: 20px;

  .review-way-setting {
    &:not(:first-child) {
      margin-top: 32px;
    }
  }
}

.review-explain {
  background: $info-sixth-color;
  border: 1px solid $info-fourth-color;
  padding: 0 20px;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 32px;

  .explain-title {
    margin-bottom: 8px;
    color: $text-color;
    font-weight: bold;
  }

  ol {
    padding: 0 0 0 16px;
    line-height: 1.5;
    color: $second-text-color;
  }

  ::v-deep .el-collapse-item__header,
  ::v-deep .el-collapse-item__wrap {
    background: $info-sixth-color;
    //border-bottom: none;
  }

  ::v-deep .el-collapse-item__content {
    padding-bottom: 0;
  }

  //::v-deep .el-collapse-item__wrap {
  //  background: $info-sixth-color;
  //  border-bottom: none;
  //}
}

.upload-btn {
  border: 1px solid $primary-color;
  color: $primary-color;
  border-radius: 5px;
  padding: 2px 8px;
  cursor: pointer;
  display: inline-block;
  span {
    font-size: 12px;
    color: $third-text-color;
  }
  &:hover {
    color: $white-color;
    background-color: $primary-color;
    span {
      color: $white-color;
    }
  }
}

.review-tab-bar {
  width: 100%;
  height: 40px;
  background: $primary-fifth-color;
  border: 1px solid $info-fourth-color;
  border-radius: 4px;
  color: $second-text-color;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  .tab-item {
    height: 40px;
    line-height: 40px;
    text-align: center;
    padding: 0 20px;
    cursor: pointer;

    &.active {
      color: $primary-color;
      background: $white-color;
      border: 1px solid $info-fourth-color;
    }

    &:first-child {
      &.active {
        border-left: none;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }
    }
  }
}
</style>
